import { useMutation } from '@tanstack/react-query';
import { findIndex } from 'lodash';
import { produce } from 'immer';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import {
  addColorToColorWidgetApi,
  editColorInColorWidgetApi,
  removeColorFromColorWidgetApi,
  setDefaultColorCodesInColorPaletteWidgetApi,
  editColorPaletteStyleApi,
} from '../../helpers/api/widgets/colorPalette';
import { defaultColors } from '../../constants/widgets';
import { GUIDE_KEYS } from '../../queries/guides';
import {
  ColorCode,
  ColorPaletteWidget,
  ColorValuesShown,
  Page,
} from '../../types';
import { queryClient } from '../../constants/app';
import { useGuideStore } from '../../stores/guideStore';

export const useColorPaletteWidgetMutations = (
  sectionId: string,
  columnIndex: number,
  widgetId: string,
) => {
  const activeGuideId = useGuideStore(s => s.activeGuideId) as string;
  const activePageId = useGuideStore(s => s.activePageId) as string;
  const activeChapterId = useGuideStore(s => s.activeChapterId) as string;
  const activeGuideVersion = useGuideStore(s => s.activeGuideVersion) as number; 

  return {
    addColor: useMutation(
      () =>
        addColorToColorWidgetApi(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId,
          columnIndex,
          '',
          defaultColors,
          activeGuideVersion,
        ),
      {
        onMutate: () => {
          updateGuideVersion(activeGuideId);
          optimisticallyAddColorToWidget(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    removeColor: useMutation(
      (options: { index: number }) =>
        removeColorFromColorWidgetApi(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId,
          options.index,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyRemoveColorFromWidget(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.index,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    editColor: useMutation(
      (options: {
        widgetColorName: string;
        widgetColorCodes: ColorCode[];
        widgetColorPosition: number;
      }) =>
        editColorInColorWidgetApi(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId,
          columnIndex,
          options.widgetColorName,
          options.widgetColorCodes,
          options.widgetColorPosition,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateColorPaletteWidget(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    changeDefaultColors: useMutation(
      (options: { colorValuesShown: ColorValuesShown }) =>
        setDefaultColorCodesInColorPaletteWidgetApi(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId,
          columnIndex,
          options.colorValuesShown,
          activeGuideVersion,
        ),
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateDefaultColors(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.colorValuesShown,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
    editStyle: useMutation(
      (mutationOptions: { style: string }) => {
        return editColorPaletteStyleApi(
          activeGuideId,
          activeChapterId,
          activePageId,
          sectionId,
          widgetId,
          columnIndex,
          mutationOptions.style,
          activeGuideVersion,
        );
      },
      {
        onMutate: variables => {
          updateGuideVersion(activeGuideId);
          optimisticallyUpdateWidgetStyle(
            activeGuideId,
            activePageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.style,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: activeGuideId,
          });
        },
      },
    ),
  };
};

const optimisticallyAddColorToWidget = (
  activeGuideId: string,
  activePageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      if (old) {
        return produce(old, draft => {
          const colorPaletteWidget = {
            ...old.sections[sectionIndex].columns[columnIndex].widgets[
              widgetIndex
            ],
          } as ColorPaletteWidget;

          colorPaletteWidget.colors.push({
            name: '',
            colorCodes: defaultColors,
          });
          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = colorPaletteWidget;
        });
      }
    },
  );
};
const optimisticallyRemoveColorFromWidget = (
  activeGuideId: string,
  activePageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  colorIndex: number,
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      if (old) {
        return produce(old, draft => {
          const colorPaletteWidget = {
            ...old.sections[sectionIndex].columns[columnIndex].widgets[
              widgetIndex
            ],
          } as ColorPaletteWidget;

          colorPaletteWidget.colors.splice(colorIndex, 1);
          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = colorPaletteWidget;
        });
      }
    },
  );
};

const optimisticallyUpdateColorPaletteWidget = (
  activeGuideId: string,
  activePageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  variables: {
    widgetColorName: string;
    widgetColorCodes: ColorCode[];
    widgetColorPosition: number;
  },
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      if (old) {
        return produce(old, draft => {
          const colorPaletteWidget = {
            ...old.sections[sectionIndex].columns[columnIndex].widgets[
              widgetIndex
            ],
          } as ColorPaletteWidget;

          colorPaletteWidget.colors[variables.widgetColorPosition].name =
            variables.widgetColorName;
          colorPaletteWidget.colors[variables.widgetColorPosition].colorCodes =
            variables.widgetColorCodes;

          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = colorPaletteWidget;
        });
      }
    },
  );
};

const optimisticallyUpdateDefaultColors = (
  activeGuideId: string,
  activePageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  variables: ColorValuesShown,
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      if (old) {
        return produce(old, draft => {
          const colorPaletteWidget = {
            ...old.sections[sectionIndex].columns[columnIndex].widgets[
              widgetIndex
            ],
          } as ColorPaletteWidget;

          colorPaletteWidget.defaultColorCodes = variables;

          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = colorPaletteWidget;
        });
      }
    },
  );
};
const optimisticallyUpdateWidgetStyle = (
  activeGuideId: string,
  activePageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  style: string,
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, activeGuideId, activePageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );

      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );

      if (old) {
        return produce(old, draft => {
          const colorPaletteWidget = {
            ...old.sections[sectionIndex].columns[columnIndex].widgets[
              widgetIndex
            ],
          } as ColorPaletteWidget;

          colorPaletteWidget.style.stylesheet = style;

          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = colorPaletteWidget;
        });
      }
    },
  );
};
